import { FaCheck } from 'react-icons/fa';
import style from './accept-offer.module.scss';
import cx from 'classnames';
import { HTMLAttributes, PropsWithChildren, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axiosAPI from '../../../utils/client/axiosAPI';
import { ButtonPrimary } from '../../components/Button/ButtonPrimary';
import { AxiosError } from 'axios'; // Import AxiosError

interface OrderResponse {
  id: number;
  number_of_order: string; // Or number
  status: string;
  email: string;
  // Add other fields you expect from OrderResource
}

interface ThankYouProps extends HTMLAttributes<HTMLDivElement> {
  setNoSideBar?: (value: boolean | ((value: boolean) => boolean)) => void;
}

// Define a type for the expected structure of backend error responses
interface BackendErrorResponse {
  message: string;
  // Add other potential error fields if your backend sends them
}

export default function AcceptOffer({
  setNoSideBar,
  ...props
}: PropsWithChildren<ThankYouProps>) {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  useEffect(() => {
    setNoSideBar?.(true);
    return () => {
      setNoSideBar?.(false);
    };
  }, [setNoSideBar]);

  const {
    data: orderData,
    isLoading,
    error,
    refetch
  } = useQuery<OrderResponse, AxiosError<BackendErrorResponse>>({
    // Use AxiosError here for the error type
    queryKey: ['offerOrder', token],
    queryFn: async () => {
      if (!token) {
        console.error('[AcceptOffer] Token is missing in queryFn.');
        throw new Error('Token is missing');
      }
      try {
        console.log(`[AcceptOffer] Fetching offer details for token: ${token}`);
        const response = await axiosAPI.get(`/offer/details/${token}`);
        console.log('[AcceptOffer] Full API response:', response);
        console.log('[AcceptOffer] API response.data:', response.data);

        if (response.data && typeof response.data.data !== 'undefined') {
          console.log(
            '[AcceptOffer] Returning response.data.data:',
            response.data.data
          );
          return response.data.data;
        } else {
          console.log(
            '[AcceptOffer] response.data.data is undefined. Returning response.data directly:',
            response.data
          );
          return response.data;
        }
      } catch (apiError) {
        // apiError will be inferred as 'unknown' or 'any' in a catch block without explicit typing.
        // We expect it to be an AxiosError if it's an API call failure.
        const axiosApiError = apiError as AxiosError<BackendErrorResponse>;
        console.error(
          '[AcceptOffer] API call failed in queryFn:',
          axiosApiError.response?.data?.message || axiosApiError.message
        );
        throw axiosApiError; // Re-throw to let useQuery handle it
      }
    },
    enabled: !!token,
    retry: false
  });

  console.log('Order data by token (from useQuery):', orderData);
  if (error) {
    // error here is now AxiosError<BackendErrorResponse>
    console.error(
      'Error fetching offer by token (from useQuery error state):',
      error.response?.data?.message || error.message
    );
  }

  const { mutate: updateOrderStatusByToken, isLoading: isUpdatingStatus } =
    useMutation<OrderResponse, AxiosError<BackendErrorResponse>, string>({
      // Use AxiosError here
      mutationFn: async (newStatus: string) => {
        if (!token) throw new Error('Token is missing for status update');
        const response = await axiosAPI.put(`/offer/process/${token}`, {
          status: newStatus
        });
        return response.data && typeof response.data.data !== 'undefined'
          ? response.data.data
          : response.data;
      },
      onSuccess: (updatedOrder) => {
        queryClient.setQueryData(['offerOrder', token], updatedOrder);
        console.log('Order status updated:', updatedOrder);
      },
      onError: (updateError) => {
        // updateError is now AxiosError<BackendErrorResponse>
        console.error(
          'Error updating order status by token:',
          updateError.response?.data?.message || updateError.message
        );
        // Now you can safely access updateError.response
        alert(
          `Greška prilikom ažuriranja statusa: ${
            updateError.response?.data?.message || updateError.message
          }`
        );
        refetch();
      }
    });

  if (isLoading)
    return (
      <div className={style.wrapper}>
        <p>Učitavanje ponude...</p>
      </div>
    );

  if (error)
    // error here is AxiosError<BackendErrorResponse>
    return (
      <div className={style.wrapper}>
        <p>
          Greška prilikom učitavanja ponude:{' '}
          {error.response?.data?.message || error.message}
        </p>
        <ButtonPrimary onClick={() => refetch()}>Pokušaj ponovo</ButtonPrimary>
        <ButtonPrimary
          style={{ marginTop: '10px' }}
          onClick={() => navigate('/')}
        >
          Nazad na prodavnicu
        </ButtonPrimary>
      </div>
    );

  if (!orderData)
    return (
      <div className={style.wrapper}>
        <p>Ponuda nije pronađena ili je link neispravan.</p>
        <ButtonPrimary onClick={() => navigate('/')}>
          Nazad na prodavnicu
        </ButtonPrimary>
      </div>
    );

  const canClientAct = orderData.status === 'accepted';

  return (
    <div {...props} className={cx(style.wrapper)}>
      {canClientAct ? (
        <>
          <div className={style.icon}>
            <FaCheck />
          </div>
          <h1 className={style.title}>
            Da li želite da prihvatite ponudu broj {orderData.number_of_order}?
          </h1>
          <p>
            Ukoliko ste zadovoljni sa ponudom koju ste dobili na{' '}
            <span className="cs-primary-color">{orderData.email}</span>,
            prihvatite klikom na dugme "Prihvati ponudu".
          </p>
          <div className={style.buttonsContainer}>
            <button
              onClick={() => updateOrderStatusByToken('client-declined')}
              disabled={isUpdatingStatus}
            >
              {isUpdatingStatus ? 'Procesiranje...' : 'Odbij ponudu'}
            </button>
            <button
              onClick={() => updateOrderStatusByToken('client-accepted')}
              disabled={isUpdatingStatus}
            >
              {isUpdatingStatus ? 'Procesiranje...' : 'Prihvati ponudu'}
            </button>
          </div>
        </>
      ) : (
        <div className={style.acceptedContainer}>
          <h1 className={style.title}>
            {orderData.status === 'client-accepted' &&
              'Ponuda je već prihvaćena.'}
            {orderData.status === 'client-declined' &&
              'Ponuda je već odbijena.'}
            {orderData.status !== 'client-accepted' &&
              orderData.status !== 'client-declined' &&
              orderData.status !== 'accepted' &&
              `Status ponude: ${
                orderData.status || 'Nepoznat'
              }. Ne može se više menjati.`}
          </h1>
          <p>Narudžbina broj: {orderData.number_of_order}</p>
          <ButtonPrimary onClick={() => navigate('/')}>
            Nazad na prodavnicu
          </ButtonPrimary>
        </div>
      )}
    </div>
  );
}
